import { useEffect, useMemo, useState } from "react";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { listPlanTiers, subscribe, type PlanTier } from "@/lib/plans";
import { pushEvent } from "@/lib/gtm";
import { redirectToPaymentGateway } from "@/lib/gateway";
import {
  PLAN_UPGRADE_EVENT,
  type PlanLimitError,
  withUpgradeResult,
} from "@/lib/upgrade";

const COPY: Record<string, { title: string; benefit: string }> = {
  subscription: {
    title: "برای راه‌اندازی دوباره سرویس، پلن توسعه‌دهنده را فعال کنید",
    benefit:
      "با فعال‌سازی دوره جدید، سرویس‌های معلق شما دوباره قابل استفاده می‌شوند",
  },
  projects: {
    title: "برای ساخت اپلیکیشن جدید، پلن توسعه‌دهنده را فعال کنید",
    benefit: "تا ۵ اپلیکیشن مستقل برای پروژه‌های جدی و در حال رشد",
  },
  services_per_project: {
    title: "برای اضافه‌کردن سرویس دوم، پلن توسعه‌دهنده را فعال کنید",
    benefit: "تا ۱۰ سرویس در هر اپلیکیشن؛ مناسب وب، دیتابیس و پردازش پس‌زمینه",
  },
  services_per_account: {
    title: "ظرفیت سرویس‌های پلن فعلی شما تکمیل است",
    benefit: "با پلن توسعه‌دهنده چند سرویس را هم‌زمان اجرا کنید",
  },
  vcpu_per_service: {
    title: "این سرویس به پردازنده بیشتری نیاز دارد",
    benefit: "تا ۲ هسته پردازنده برای هر سرویس در پلن توسعه‌دهنده",
  },
  ram_per_service: {
    title: "این سرویس برای اجرای پایدار به حافظه بیشتری نیاز دارد",
    benefit: "تا ۸ گیگابایت رم برای هر سرویس در پلن توسعه‌دهنده",
  },
  storage: {
    title: "برای افزایش فضای ذخیره‌سازی، پلن خود را ارتقا دهید",
    benefit: "فضای ذخیره‌سازی با رشد خودکار تا ۵۰ گیگابایت برای هر سرویس",
  },
  volumes_per_service: {
    title: "برای افزودن فضای ذخیره‌سازی بیشتر، پلن خود را ارتقا دهید",
    benefit: "تا ۵ دیسک پایدار برای هر سرویس",
  },
  custom_domains: {
    title: "برای اتصال دامنه‌های بیشتر، پلن خود را ارتقا دهید",
    benefit: "تا ۱۰ دامنه اختصاصی در پلن توسعه‌دهنده",
  },
  preview_envs: {
    title: "برای محیط پیش‌نمایش بیشتر، پلن خود را ارتقا دهید",
    benefit: "تا ۳ محیط پیش‌نمایش برای بررسی تغییرات قبل از انتشار",
  },
  cron_jobs: {
    title: "برای کارهای زمان‌بندی‌شده بیشتر، پلن خود را ارتقا دهید",
    benefit: "تا ۵ کار زمان‌بندی‌شده در پلن توسعه‌دهنده",
  },
};

const fa = (n: number) => n.toLocaleString("fa-IR");

export function UpgradeModalProvider() {
  const [reason, setReason] = useState<PlanLimitError | null>(null);
  const [tier, setTier] = useState<PlanTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<PlanLimitError>).detail;
      setReason(detail);
      setCheckoutError("");
      pushEvent("upgrade_prompt_viewed", {
        resource: detail.resource,
        current_plan: detail.plan,
        recommended_plan: detail.recommended_plan || "hobby",
        source_page: detail.source_page || window.location.pathname,
      });
      listPlanTiers()
        .then((tiers) =>
          setTier(
            tiers.find(
              (p) => p.plan === (detail.recommended_plan || "hobby"),
            ) || null,
          ),
        )
        .catch(() => setTier(null));
    };
    window.addEventListener(PLAN_UPGRADE_EVENT, handler);
    return () => window.removeEventListener(PLAN_UPGRADE_EVENT, handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "1") {
      pushEvent("blocked_action_resumed", {
        source_page: window.location.pathname,
      });
      params.delete("upgraded");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${params.size ? `?${params}` : ""}${window.location.hash}`,
      );
    }
  }, []);

  const copy = useMemo(
    () =>
      COPY[reason?.resource || ""] || {
        title: "برای ادامه، پلن توسعه‌دهنده را فعال کنید",
        benefit: "منابع و امکانات بیشتر برای اجرای پایدار سرویس‌های شما",
      },
    [reason],
  );

  if (!reason) return null;

  const close = () => {
    pushEvent("upgrade_prompt_dismissed", {
      resource: reason.resource,
      current_plan: reason.plan,
      recommended_plan: reason.recommended_plan || "hobby",
      source_page: reason.source_page || window.location.pathname,
    });
    setReason(null);
    setTier(null);
  };

  const startCheckout = async () => {
    const plan = reason.recommended_plan || "hobby";
    setLoading(true);
    setCheckoutError("");
    pushEvent("upgrade_prompt_accepted", {
      resource: reason.resource,
      current_plan: reason.plan,
      recommended_plan: plan,
      source_page: reason.source_page || window.location.pathname,
    });
    try {
      const returnTo = withUpgradeResult(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      sessionStorage.setItem("upgradeReturnTo", returnTo);
      sessionStorage.setItem("pendingPlan", plan);
      sessionStorage.setItem("upgradeResource", reason.resource);
      pushEvent("plan_checkout_started", {
        plan,
        resource: reason.resource,
        source_page: reason.source_page || window.location.pathname,
      });
      const { paymentUrl } = await subscribe(plan);
      redirectToPaymentGateway(paymentUrl);
    } catch (error: any) {
      pushEvent("plan_checkout_failed", {
        plan,
        resource: reason.resource,
        stage: "checkout_creation",
      });
      setCheckoutError(
        error?.response?.data?.error || "اتصال به درگاه پرداخت ناموفق بود.",
      );
      setLoading(false);
    }
  };

  return (
    <ConfirmModal
      open
      title={copy.title}
      confirmLabel="فعال‌سازی توسعه‌دهنده و ادامه"
      cancelLabel="فعلاً نه"
      tone="primary"
      loading={loading}
      onConfirm={startCheckout}
      onClose={close}
      message={
        <div className="space-y-3">
          <p>{reason.error}</p>
          <div className="rounded-lg border border-gerdoo-200 bg-gerdoo-50/70 p-3 text-gerdoo-900 dark:border-gerdoo-500/20 dark:bg-gerdoo-500/10 dark:text-gerdoo-100">
            <p className="font-semibold">{copy.benefit}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="block text-muted-foreground">پلن فعلی</span>
                <span className="font-semibold">برای تفریح</span>
              </div>
              <div>
                <span className="block text-muted-foreground">
                  پلن پیشنهادی
                </span>
                <span className="font-semibold">توسعه‌دهنده</span>
              </div>
            </div>
          </div>
          {tier && (
            <div className="flex items-end justify-between gap-3">
              <span>مبلغ نهایی با مالیات</span>
              <span className="font-semibold text-foreground">
                {fa(tier.total_toman ?? tier.price_toman)} تومان
              </span>
            </div>
          )}
          <p className="text-xs">
            مبلغ پایهٔ پلن به اعتبار کیف پول شما اضافه می‌شود؛ مالیات جداگانه
            محاسبه شده و هزینه مصرف واقعی سرویس‌ها از اعتبار کسر خواهد شد.
          </p>
          {checkoutError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-red-700 dark:bg-red-500/10 dark:text-red-300">
              {checkoutError}
            </p>
          )}
        </div>
      }
    />
  );
}
